import { useState } from "react";

import { ApiError } from "../api/errors";
import type { InventoryItem, ManualReasonCode } from "../api/types";
import { ConfirmDialog } from "../products/ConfirmDialog";
import { ErrorNotice } from "../components/ui";
import { REASON_LABELS, REASON_ORDER, variantLabel } from "./labels";
import { useAdjustStock } from "./useInventory";

/**
 * Add stock, or take it away — and say why.
 *
 * WHAT THIS DIALOG CANNOT EXPRESS
 * A new total. There is no "set stock to" box, because a total is a number
 * read off a screen that may already be stale and writing it would silently
 * discard whatever happened in between. The operator says what they DID — five
 * arrived, three were broken — and the server works out the rest from the
 * quantity it really holds.
 *
 * WHAT IT SENDS BESIDE THE DELTA
 * `expected_stock_quantity`: the number this dialog was opened against. If the
 * item has moved since — a sale, a cancellation, another administrator — the
 * backend refuses with 409 and the request is NEVER re-applied to the new
 * number. Somebody who typed "remove 3" against 12 did not thereby agree to
 * remove 3 from 8.
 *
 * The preview is the honest part of the form: it shows the arithmetic the
 * server is about to do, so a mistake is visible before it is committed rather
 * than at the next stock count.
 */
export function AdjustStockDialog({
  item,
  onClose,
}: {
  item: InventoryItem;
  onClose: () => void;
}) {
  const [operation, setOperation] = useState<"add" | "remove">("add");
  const [quantityText, setQuantityText] = useState("1");
  const [reason, setReason] = useState<ManualReasonCode>("stock_received");
  const [note, setNote] = useState("");

  const adjust = useAdjustStock(item.inventory_item_id);

  // Null for an accessory that comes one way, and the whole fact is then left
  // out rather than written as "Variant: —". This is prose about one item, not
  // a table with a column to fill: a dash here would be a blank where a choice
  // is implied, which still says there was one.
  const variant = variantLabel(item);

  const quantity = Number.parseInt(quantityText, 10);
  const quantityValid = Number.isInteger(quantity) && quantity >= 1;
  const delta = operation === "add" ? quantity : -quantity;
  const result = quantityValid ? item.stock_quantity + delta : null;
  const wouldGoNegative = result !== null && result < 0;
  const noteRequired = reason === "other";
  const noteMissing = noteRequired && note.trim() === "";

  // Refused here AND on the server. The client check exists so the operator is
  // told before they press the button; the server check exists because a
  // client is not what makes a rule true.
  const canConfirm =
    quantityValid && !wouldGoNegative && !noteMissing && !adjust.isPending;

  // A conflict carries the quantity the item REALLY holds, so the operator is
  // shown the truth rather than asked to reload and work out what happened.
  const conflictQuantity =
    adjust.error instanceof ApiError && adjust.error.kind === "conflict"
      ? (adjust.error.detail as { current_stock_quantity?: number } | null)
          ?.current_stock_quantity
      : undefined;

  return (
    <ConfirmDialog
      title="Adjust stock"
      confirmLabel={operation === "add" ? "Add stock" : "Remove stock"}
      busy={adjust.isPending}
      confirmDisabled={!canConfirm}
      onCancel={onClose}
      onConfirm={() => {
        if (!canConfirm) return;
        adjust.mutate(
          {
            operation,
            quantity,
            expected_stock_quantity: item.stock_quantity,
            reason_code: reason,
            note: note.trim() ? note.trim() : null,
          },
          { onSuccess: onClose },
        );
      }}
    >
      <div className="stack" data-testid="adjust-stock-dialog">
        <dl className="inventory__facts">
          <dt>Product</dt>
          <dd>{item.product_name}</dd>
          {variant !== null ? (
            <>
              <dt>Variant</dt>
              <dd>{variant}</dd>
            </>
          ) : null}
          <dt>Current stock</dt>
          <dd className="num" data-testid="adjust-current">
            {item.stock_quantity}
          </dd>
        </dl>

        <fieldset className="inventory__field">
          <legend className="inventory__label">Operation</legend>
          <div className="inventory__choiceRow">
            <label
              className={
                operation === "add"
                  ? "inventory__choice is-selected"
                  : "inventory__choice"
              }
            >
              <input
                type="radio"
                name="adjust-operation"
                value="add"
                checked={operation === "add"}
                onChange={() => setOperation("add")}
              />
              <span>Add stock</span>
            </label>
            <label
              className={
                operation === "remove"
                  ? "inventory__choice is-selected"
                  : "inventory__choice"
              }
            >
              <input
                type="radio"
                name="adjust-operation"
                value="remove"
                checked={operation === "remove"}
                onChange={() => setOperation("remove")}
              />
              <span>Remove stock</span>
            </label>
          </div>
        </fieldset>

        <div className="inventory__field">
          <label className="inventory__label" htmlFor="adjust-quantity">
            Quantity
          </label>
          <input
            id="adjust-quantity"
            className="num"
            type="number"
            min={1}
            step={1}
            style={{ width: 140 }}
            value={quantityText}
            autoFocus
            onChange={(event) => setQuantityText(event.target.value)}
          />
        </div>

        {/* The arithmetic the server is about to do, in the operator's own
            terms. Not a second calculation of the result — the response
            replaces this the moment it arrives. */}
        <p className="inventory__preview" data-testid="adjust-preview">
          {quantityValid ? (
            <>
              Current <span className="num">{item.stock_quantity}</span>
              {" · "}
              {operation === "add" ? "Add" : "Remove"}{" "}
              <span className="num">{quantity}</span>
              {" · "}
              Result <span className="num">{result}</span>
            </>
          ) : (
            "Enter a quantity of one or more."
          )}
        </p>

        {wouldGoNegative ? (
          <div className="notice notice--warning" role="alert">
            This item has {item.stock_quantity} in stock, so {quantity} cannot be
            removed.
          </div>
        ) : null}

        <div className="inventory__field">
          <label className="inventory__label" htmlFor="adjust-reason">
            Reason
          </label>
          <select
            id="adjust-reason"
            value={reason}
            onChange={(event) =>
              setReason(event.target.value as ManualReasonCode)
            }
          >
            {REASON_ORDER.map((code) => (
              <option key={code} value={code}>
                {REASON_LABELS[code]}
              </option>
            ))}
          </select>
        </div>

        <div className="inventory__field">
          <label className="inventory__label" htmlFor="adjust-note">
            Note {noteRequired ? "(required)" : "(optional)"}
          </label>
          <textarea
            id="adjust-note"
            rows={2}
            maxLength={500}
            value={note}
            aria-invalid={noteMissing}
            onChange={(event) => setNote(event.target.value)}
          />
          {noteMissing ? (
            <p className="field-error">
              “Other” says nothing on its own. Write what happened.
            </p>
          ) : null}
        </div>

        {adjust.error ? (
          <>
            <ErrorNotice error={adjust.error} />
            {conflictQuantity !== undefined ? (
              <p className="muted" data-testid="adjust-conflict">
                This item now holds{" "}
                <span className="num">{conflictQuantity}</span>. Close this
                dialog and open it again to work from the current figure.
              </p>
            ) : null}
          </>
        ) : null}
      </div>
    </ConfirmDialog>
  );
}
